import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FilePdf, WhatsappLogo, CheckCircle, Package, CalendarBlank, ShoppingCart, ClockCounterClockwise } from "@phosphor-icons/react";
import ExportTemplateConfig from "@/components/ExportTemplateConfig";
import ExportSelectorDialog from "@/components/ExportSelectorDialog";
import ShoppingListBuilderDialog from "@/components/ShoppingListBuilderDialog";
import { formatCurrency } from "@/pages/sheetUtils";


export default function ShoppingListResult({
  shoppingList, stockDeducted, purchaseConfirmed, confirming,
  onConfirmPurchase, onGeneratePDF, onShareWhatsApp,
  onSaveList, savingList, listName, onListNameChange,
}) {
  return (
    <Card data-testid="shopping-list-result">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShoppingCart size={20} />
            Lista de Compras Gerada
            {stockDeducted && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded border border-green-200">
                Estoque descontado
              </span>
            )}
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={onGeneratePDF} data-testid="shopping-list-pdf-btn">
              <FilePdf size={16} className="mr-1" /> PDF
            </Button>
            <Button
              variant="outline" size="sm" onClick={onShareWhatsApp}
              className="text-green-700 border-green-200 hover:bg-green-50"
              data-testid="shopping-list-whatsapp-btn"
            >
              <WhatsappLogo size={16} className="mr-1" /> WhatsApp
            </Button>
            <Button
              size="sm" onClick={onConfirmPurchase}
              disabled={confirming || purchaseConfirmed || shoppingList.items.every((i) => i.packages_needed === 0)}
              className={purchaseConfirmed ? "bg-green-600 text-white hover:bg-green-600 cursor-default" : "bg-zinc-900 text-white hover:bg-zinc-800"}
              data-testid="confirm-purchase-btn"
            >
              <CheckCircle size={16} className="mr-1" />
              {confirming ? "Confirmando..." : purchaseConfirmed ? "Compra Confirmada" : "Confirmar Compra"}
            </Button>
          </div>
        </div>
        {/* Save list row */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
          <Input
            value={listName}
            onChange={(e) => onListNameChange(e.target.value)}
            placeholder="Nome da lista (opcional)"
            className="h-8 text-sm flex-1 max-w-xs"
            data-testid="save-list-name-input"
          />
          <Button
            variant="outline" size="sm" onClick={onSaveList} disabled={savingList}
            className="text-blue-700 border-blue-200 hover:bg-blue-50"
            data-testid="save-list-btn"
          >
            <ClockCounterClockwise size={14} className="mr-1" />
            {savingList ? "Salvando..." : "Salvar Lista"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary */}
        <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
          <div className="bg-zinc-50 rounded-lg p-3 text-center" data-testid="shopping-list-items-count">
            <p className="text-lg md:text-2xl font-bold text-zinc-900">{shoppingList.items.length}</p>
            <p className="text-xs text-zinc-500">Ingredientes</p>
          </div>
          <div className="bg-zinc-50 rounded-lg p-3 text-center" data-testid="shopping-list-packages-count">
            <p className="text-lg md:text-2xl font-bold text-zinc-900">{shoppingList.items.reduce((acc, i) => acc + i.packages_needed, 0)}</p>
            <p className="text-xs text-zinc-500">Pacotes Total</p>
          </div>
          <div className="bg-zinc-50 rounded-lg p-3 text-center" data-testid="shopping-list-total-cost">
            <p className="text-lg md:text-2xl font-bold text-zinc-900">{formatCurrency(shoppingList.total_cost)}</p>
            <p className="text-xs text-zinc-500">Custo Total</p>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-zinc-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs font-semibold">Ingrediente</TableHead>
                <TableHead className="text-xs font-semibold text-right">Qtd. Necessária</TableHead>
                {stockDeducted && <TableHead className="text-xs font-semibold text-right">Em Estoque</TableHead>}
                <TableHead className="text-xs font-semibold text-right">Tam. Pacote</TableHead>
                <TableHead className="text-xs font-semibold text-right">Pacotes</TableHead>
                <TableHead className="text-xs font-semibold text-right">Preço/Pacote</TableHead>
                <TableHead className="text-xs font-semibold text-right">Custo Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shoppingList.items.map((item) => (
                <TableRow key={item.ingredient_id} data-testid={`shopping-item-${item.ingredient_id}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium">{item.ingredient_name}</p>
                      <p className="text-xs text-zinc-400">
                        {item.sheets.map((s) => s.is_extra ? "Avulso" : `${s.name} (x${s.quantity})`).join(", ")}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {stockDeducted && item.original_quantity ? item.original_quantity : item.total_quantity_needed} {item.unit}
                  </TableCell>
                  {stockDeducted && (
                    <TableCell className="text-right text-sm text-green-700 font-mono">{item.in_stock || 0} {item.unit}</TableCell>
                  )}
                  <TableCell className="text-right text-sm">{item.package_size} {item.package_unit || item.unit}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{item.packages_needed}</TableCell>
                  <TableCell className="text-right text-sm">{formatCurrency(item.package_price)}</TableCell>
                  <TableCell className="text-right text-sm font-bold">{formatCurrency(item.total_cost)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Total Footer */}
        <div className="mt-4 flex justify-end">
          <div className="bg-zinc-950 text-white rounded-lg px-6 py-3" data-testid="shopping-list-grand-total">
            <p className="text-xs text-zinc-400">Custo Total Estimado</p>
            <p className="text-xl font-bold">{formatCurrency(shoppingList.total_cost)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
